/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import { useAutosuggestItemContent } from 'sarsaparilla';

import {
    getSuggestionValue,
    isContentSuggestion,
    isHistorySuggestion,
    isInventorySuggestion,
} from './LocationSuggestion.utils';

import type { LocationSuggestionOptionType } from './LocationSuggestion.types';

import styles from './LocationSuggestion.module.scss';

interface LocationContentProps {
    suggestion: LocationSuggestionOptionType;
    searchText: string;
}

export function LocationContent({ suggestion, searchText }: LocationContentProps) {
    return (
        <div translate="no">
            <LocationTitle suggestion={suggestion} searchText={searchText} />

            <LocationSubtitle suggestion={suggestion} />
        </div>
    );
}

interface LocationTitleProps {
    suggestion: LocationSuggestionOptionType;
    searchText: string;
}

export function LocationTitle({ suggestion, searchText }: LocationTitleProps) {
    const { labelProps } = useAutosuggestItemContent();
    const suggestionText = getSuggestionValue(suggestion);

    const trimmedQuery = searchText.trim();
    const matches = match(suggestionText, trimmedQuery);
    const parts = parse(suggestionText, matches);

    return (
        <div data-testid="suggestion-title" {...labelProps}>
            {parts.map((part, index) => {
                const className = part.highlight ? 'highlight' : undefined;

                return (
                    <span className={className} key={index}>
                        {part.text}
                    </span>
                );
            })}
        </div>
    );
}

export function LocationSubtitle({
    suggestion,
}: {
    suggestion: LocationSuggestionOptionType;
}) {
    const { descriptionProps } = useAutosuggestItemContent();
    const hasEntityType =
        isContentSuggestion(suggestion) || isInventorySuggestion(suggestion);
    const hasParentName = hasEntityType && suggestion.parent_name;
    const hasEntityAndLocation =
        (isHistorySuggestion(suggestion) || isInventorySuggestion(suggestion)) &&
        suggestion.entity_type &&
        suggestion.city &&
        suggestion.state_code;

    return (
        <div
            className={styles.LocationSubtitle}
            data-testid="suggestion-subtitle"
            {...descriptionProps}
        >
            {hasParentName && <span>{suggestion.parent_name}</span>}
            {hasEntityAndLocation && (
                <span>{`${hasParentName ? ' | ' : ''}Near ${suggestion.city}, ${suggestion.state_code}`}</span>
            )}
        </div>
    );
}
